import React, { useState } from 'react';
import { Loader2Icon, UserIcon } from 'lucide-react';
import { FormSection } from './FormSection';
import { Car } from '../types/car';
import { formatARS } from '../utils/format';
import { ANNUAL_RATE, FINANCING_TERMS, calcInstallment } from '../utils/financing';
import { formatCuil, isValidCuil } from '../utils/cuil';
import { submitFinancingRequest } from '../utils/financingRequest';
import { CarSummary } from './CarSummary';
import { CreditProfile, CreditProfileFields, emptyCreditProfile, validateCreditProfile } from './CreditProfileFields';
import { FormField, inputClass } from './FormField';
import { SubmitError } from './SubmitError';
import { SuccessState } from './SuccessState';
type Applicant = {
  firstName: string;
  lastName: string;
  cuil: string;
  email: string;
  phone: string;
};
interface FinancingSimulatorProps {
  car: Car;
  onClose: () => void;
}
const MIN_DOWN = 0;
const MAX_DOWN = 90;
function initialDownPct(car: Car): number {
  const pct = Math.round(car.downPayment / car.cashPrice * 100 / 5) * 5;
  return Math.min(MAX_DOWN, Math.max(MIN_DOWN, pct));
}
export function FinancingSimulator({
  car,
  onClose
}: FinancingSimulatorProps) {
  const [downPct, setDownPct] = useState(initialDownPct(car));
  const [term, setTerm] = useState(car.installments);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [applicant, setApplicant] = useState<Applicant>({
    firstName: '',
    lastName: '',
    cuil: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Applicant, string>>>({});
  const [credit, setCredit] = useState<CreditProfile>(emptyCreditProfile);
  const [creditErrors, setCreditErrors] = useState<Partial<Record<keyof CreditProfile, string>>>({});
  const downPayment = Math.round(car.cashPrice * downPct / 100);
  const financed = car.cashPrice - downPayment;
  const monthly = calcInstallment(financed, term);
  const totalFinanced = monthly * term;
  if (status === 'success') {
    return <SuccessState title="Muchas gracias" message="En un plazo de 4 hs a 12 hs estará recibiendo su respuesta." onClose={onClose} data-id="element-241" data-mp="1fmqivgtrgw5w" />;
  }
  const update = (key: keyof Applicant) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = key === 'cuil' ? formatCuil(e.target.value) : e.target.value;
    setApplicant(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Partial<Record<keyof Applicant, string>> = {};
    if (!applicant.firstName.trim()) next.firstName = 'Ingresá tu nombre';
    if (!applicant.lastName.trim()) next.lastName = 'Ingresá tu apellido';
    if (!isValidCuil(applicant.cuil)) next.cuil = 'Ingresá un CUIL válido (11 números)';
    if (!/^\S+@\S+\.\S+$/.test(applicant.email)) next.email = 'Ingresá un email válido';
    if (applicant.phone.replace(/\D/g, '').length < 8) next.phone = 'Ingresá un teléfono válido';
    const nextCredit = validateCreditProfile(credit);
    setErrors(next);
    setCreditErrors(nextCredit);
    if (Object.keys(next).length > 0 || Object.keys(nextCredit).length > 0) return;
    setStatus('loading');
    try {
      await submitFinancingRequest({
        nombre: applicant.firstName.trim(),
        apellido: applicant.lastName.trim(),
        cuil: applicant.cuil,
        telefono: applicant.phone.trim(),
        email: applicant.email.trim(),
        autoInteres: `${car.id} · ${car.brand} ${car.model} ${car.version} ${car.year}`,
        anticipo: `${formatARS(downPayment)} (${downPct}%)`,
        cuotas: `${term} cuotas de ${formatARS(monthly)}`,
        precioContado: formatARS(car.cashPrice),
        montoFinanciar: String(financed),
        valorCuota: String(monthly),
        situacionLaboral: credit.employment,
        ingresosMensuales: credit.income.replace(/\D/g, ''),
        antiguedad: credit.seniority,
        cuentaBancaria: credit.bankAccount,
        garante: credit.guarantor,
        permuta: credit.tradeIn
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  return <form onSubmit={handleSubmit} noValidate className="space-y-6" data-id="element-242" data-mp="2cevg5izoug05">
      <CarSummary car={car} data-id="element-243" data-mp="2h6vmpua9k75p" />

      <div data-id="element-244" data-mp="2h758t6s6tu64">
        <div className="flex items-baseline justify-between" data-id="element-245" data-mp="2h7ohrxzv1880">
          <label htmlFor="down-payment" className="text-sm font-medium text-white" data-id="element-246" data-mp="2m685a2u3qs2h">
            Anticipo
          </label>
          <p className="text-sm tabular-nums text-white" data-id="element-247" data-mp="2q5gz17p56py1">
            <span className="font-semibold" data-id="element-248" data-mp="2v5mvd16cx0mg">{formatARS(downPayment)}</span>
            <span className="ml-1.5 text-muted" data-id="element-249" data-mp="2v4bub94aq6z1">({downPct}%)</span>
          </p>
        </div>
        <input id="down-payment" type="range" min={MIN_DOWN} max={MAX_DOWN} step={5} value={downPct} onChange={e => setDownPct(Number(e.target.value))} className="mt-3 w-full accent-neon" data-id="element-250" data-mp="302vfh7b2feqc" />
        <div className="mt-1 flex justify-between text-xs text-muted" data-id="element-251" data-mp="y90dd6ipgrqh">
          <span data-id="element-252" data-mp="tfxrlkk66mku">{MIN_DOWN}%</span>
          <span data-id="element-253" data-mp="tfny3mzx1xae">{MAX_DOWN}%</span>
        </div>
      </div>

      <fieldset data-id="element-254" data-mp="oh2xhmgr5blf">
        <legend className="text-sm font-medium text-white" data-id="element-255" data-mp="oins1c32guja">Cantidad de cuotas</legend>
        <div className="mt-3 grid grid-cols-5 gap-2" data-id="element-256" data-mp="jjuexkroz5jn">
          {FINANCING_TERMS.map(t => {
          const active = t === term;
          return <button key={t} type="button" aria-pressed={active} onClick={() => setTerm(t)} className={`h-11 rounded-lg border text-sm font-semibold tabular-nums transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon ${active ? 'border-neon bg-neon text-ink' : 'border-line text-white hover:border-neon/60 hover:text-neon'}`} data-id="element-257" data-mp="1c7gttp6ddwlt">
                {t}
              </button>;
        })}
        </div>
      </fieldset>

      <div className="rounded-xl border border-line bg-ink p-5" data-id="element-258" data-mp="2j1o28c8kyqss">
        <p className="text-sm text-muted" data-id="element-259" data-mp="2j0v582zfbfnu">Cuota mensual estimada</p>
        <p className="font-display text-4xl font-bold tabular-nums text-neon" aria-live="polite" data-id="element-260" data-mp="252ohhfo6uw1w">
          {formatARS(monthly)}
        </p>
        <dl className="mt-4 grid grid-cols-2 gap-4 border-t border-line pt-4 text-sm" data-id="element-261" data-mp="2a32ocue1zzyc">
          <div data-id="element-262" data-mp="1vasfvumw8nsx">
            <dt className="text-muted" data-id="element-263" data-mp="1v9z8crg41u9q">Monto a financiar</dt>
            <dd className="font-semibold tabular-nums text-white" data-id="element-264" data-mp="203lkmiid3w50">{formatARS(financed)}</dd>
          </div>
          <div data-id="element-265" data-mp="1mcox363am54t">
            <dt className="text-muted" data-id="element-266" data-mp="1md8caf1k6ff5">Total en cuotas</dt>
            <dd className="font-semibold tabular-nums text-white" data-id="element-267" data-mp="1r9p2rfw8d5ve">{formatARS(totalFinanced)}</dd>
          </div>
        </dl>
        <p className="mt-4 text-xs leading-5 text-muted" data-id="element-268" data-mp="1cdr9c5doyfmv">
          Cuotas fijas en pesos. TNA {(ANNUAL_RATE * 100).toFixed(1).replace('.', ',')}%. Simulación sujeta a aprobación crediticia.
        </p>
      </div>

      <FormSection icon={UserIcon} title="Tus datos para la solicitud" description="Los usamos para preparar tu propuesta de financiación." data-id="element-269" data-mp="xqsjgeuhfta9">
        <div className="grid gap-4 sm:grid-cols-2" data-id="element-270" data-mp="17p7znmrz1i4z">
          <FormField label="Nombre" htmlFor="fs-first-name" error={errors.firstName} data-id="element-271" data-mp="17pzxz5cydtyy">
            <input id="fs-first-name" className={inputClass} value={applicant.firstName} onChange={update('firstName')} autoComplete="given-name" data-id="element-272" data-mp="12o0s9otqqkyu" />
          </FormField>
          <FormField label="Apellido" htmlFor="fs-last-name" error={errors.lastName} data-id="element-273" data-mp="1cokyl5w4afnx">
            <input id="fs-last-name" className={inputClass} value={applicant.lastName} onChange={update('lastName')} autoComplete="family-name" data-id="element-274" data-mp="1qfppnjlf5zmn" />
          </FormField>
          <FormField label="CUIL" htmlFor="fs-cuil" error={errors.cuil} data-id="element-275" data-mp="1li69ge55vcs2">
            <input id="fs-cuil" inputMode="numeric" placeholder="20-12345678-9" className={inputClass} value={applicant.cuil} onChange={update('cuil')} data-id="element-276" data-mp="20dvp7ujr6toc" />
          </FormField>
          <FormField label="Teléfono" htmlFor="fs-phone" error={errors.phone} data-id="element-277" data-mp="l08hsga63d8r">
            <input id="fs-phone" type="tel" placeholder="11 5555 5555" className={inputClass} value={applicant.phone} onChange={update('phone')} autoComplete="tel" data-id="element-278" data-mp="q0e55k9emdl1" />
          </FormField>
          <FormField label="Email" htmlFor="fs-email" error={errors.email} className="sm:col-span-2" data-id="element-279" data-mp="2aaai2e71o66">
            <input id="fs-email" type="email" className={inputClass} value={applicant.email} onChange={update('email')} autoComplete="email" data-id="element-280" data-mp="79wm67ediwcv" />
          </FormField>
        </div>
      </FormSection>

      <CreditProfileFields values={credit} errors={creditErrors} onChange={(key, value) => {
      setCredit(prev => ({
        ...prev,
        [key]: value
      }));
      setCreditErrors(prev => ({
        ...prev,
        [key]: undefined
      }));
    }} data-id="element-281" data-mp="3fp6mb9ypdri0" />

      {status === 'error' && <SubmitError data-id="element-282" data-mp="1ntnhqcbby6ki" />}

      <button type="submit" disabled={status === 'loading'} className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-neon text-sm font-semibold text-ink transition-colors duration-150 hover:bg-neon-bright disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon focus-visible:ring-offset-2 focus-visible:ring-offset-panel" data-id="element-283" data-mp="1nt47zivu2ohk">
        {status === 'loading' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-284" data-mp="153xq1y2v60wt" />}
        {status === 'loading' ? 'Enviando…' : status === 'error' ? 'Reintentar' : 'Enviar solicitud'}
      </button>
    </form>;
}